import React, { useState, useEffect } from "react";
import {
  Appointment,
  AppointmentFormData,
} from "../../services/appointmentService";
import AppointmentStatusMenu from "./AppointmentStatusMenu";
import AppointmentModal from "./AppointmentModal";
import getIcon from "../../utils/Icons";

// Add SelectedService interface
interface SelectedService {
  service: {
    id: number;
    name: string;
    base_fee: number;
    clinic_percentage: number;
    provider_percentage: number;
    owner_percentage: number;
    referrer_percentage: number;
  };
  referrer_name: string;
  customPrice?: number;
  pricingReason?: string;
}

interface AppointmentCalendarProps {
  appointments: Appointment[];
  currentDate: Date;
  view: "day" | "week" | "month";
  doctors: any[];
  patients: any[]; // Add patients array
  selectedDoctors: number[];
  onDateChange: (date: Date) => void;
  onAppointmentClick: (appointment: Appointment) => void;
  onSlotClick: (slot: {
    date: string;
    time: string;
    doctorId?: number;
  }) => void;
  onAppointmentDrop: (
    appointmentId: number,
    newDate: string,
    newTime: string
  ) => void;
  onAppointmentAction?: (
    action: "confirm" | "arrive" | "complete" | "cancel" | "no_show" | "delete",
    appointmentId: number,
    reason?: string,
    services?: SelectedService[]
  ) => Promise<void>;
  onSaveCustomPricing?: (
    appointmentId: number,
    services: SelectedService[]
  ) => Promise<void>;
  onAppointmentSave?: (data: AppointmentFormData) => Promise<void>; // Add save callback
  onAppointmentEdit?: () => void; // Add edit callback
  onAppointmentDelete?: (appointmentId: number) => Promise<void>; // Add delete callback
  isLoading: boolean;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  appointments,
  currentDate,
  view,
  doctors,
  patients,
  selectedDoctors,
  onDateChange,
  onAppointmentClick,
  onSlotClick,
  onAppointmentDrop,
  onAppointmentAction,
  onSaveCustomPricing,
  onAppointmentSave,
  onAppointmentEdit,
  onAppointmentDelete,
  isLoading,
}) => {
  const [draggedAppointment, setDraggedAppointment] =
    useState<Appointment | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{
    date: string;
    time: string;
    doctorId: number;
  } | null>(null);

  // Status menu state
  const [statusMenu, setStatusMenu] = useState<{
    appointment: Appointment;
    position: { x: number; y: number };
  } | null>(null);

  // ADD: Book appointment modal state
  const [bookAppointmentModal, setBookAppointmentModal] = useState<{
    isOpen: boolean;
    prefilledPatient?: { id: number; name: string; phone: string };
  }>({
    isOpen: false,
    prefilledPatient: undefined,
  });

  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Generate time slots (8 AM to 6 PM in 15-minute intervals)
  const generateTimeSlots = (): string[] => {
    const slots: string[] = [];
    for (let hour = 8; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === 18 && minute > 0) break; // Stop at 6:00 PM
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        slots.push(time);
      }
    }
    return slots;
  };

  // Generate date columns based on view
  const generateDateColumns = (): Date[] => {
    if (view === "day") {
      return [new Date(currentDate)];
    } else if (view === "week") {
      const dates: Date[] = [];
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
      return dates;
    } else {
      // Month view - show all days of the month
      const dates: Date[] = [];
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      // Get first and last day of the month
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      // Generate all days in the month
      for (let day = 1; day <= lastDay.getDate(); day++) {
        dates.push(new Date(year, month, day));
      }

      return dates;
    }
  };

  const dateColumns = generateDateColumns();
  const filteredDoctors = doctors.filter((doctor) =>
    selectedDoctors.includes(doctor.id)
  );

  // FIXED: Centralized slot width calculation that works for ALL screen sizes
  const calculateSlotWidth = (): number => {
    const screenWidth = window.innerWidth;
    const totalSlots = filteredDoctors.length * dateColumns.length;

    if (isMobile) {
      const timeColumnWidth = screenWidth <= 480 ? 50 : 60;
      const padding = 32;

      let slotWidth;
      if (screenWidth <= 360) {
        slotWidth = Math.max(
          120,
          (screenWidth - timeColumnWidth - padding) / Math.min(totalSlots, 2.5)
        );
      } else if (screenWidth <= 480) {
        slotWidth = Math.max(
          140,
          (screenWidth - timeColumnWidth - padding) / Math.min(totalSlots, 3)
        );
      } else {
        slotWidth = Math.max(
          160,
          (screenWidth - timeColumnWidth - padding) / Math.min(totalSlots, 4)
        );
      }

      return Math.floor(slotWidth);
    } else if (isTablet) {
      // Tablet: Calculate based on available space
      const timeColumnWidth = 80;
      const padding = 40;
      const availableWidth = screenWidth - timeColumnWidth - padding;
      const slotWidth = Math.max(150, availableWidth / totalSlots);
      return Math.floor(slotWidth);
    } else {
      // FIXED: Desktop now also calculates consistent width
      const timeColumnWidth = 80;
      const padding = 60;
      const availableWidth = screenWidth - timeColumnWidth - padding;
      const minSlotWidth = 180; // Minimum width for desktop
      const slotWidth = Math.max(minSlotWidth, availableWidth / totalSlots);
      return Math.floor(slotWidth);
    }
  };

  // Use consistent slot width across all components
  const slotWidth = calculateSlotWidth();
  const timeSlots = generateTimeSlots();

  // Helper functions
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  const isCurrentMonth = (date: Date): boolean => {
    return (
      date.getMonth() === currentDate.getMonth() &&
      date.getFullYear() === currentDate.getFullYear()
    );
  };

  const getAppointmentsForDate = (date: Date): Appointment[] => {
    const dateStr = formatDate(date);
    return appointments.filter((apt) => {
      let aptDate: string;
      if (apt.appointment_date.includes("T")) {
        aptDate = apt.appointment_date.split("T")[0];
      } else {
        aptDate = apt.appointment_date;
      }
      return aptDate === dateStr;
    });
  };
  const isPastTime = (date: Date, time: string): boolean => {
    const now = new Date();
    const slotTime = new Date(`${formatDate(date)}T${time}:00`);
    return slotTime < now;
  };

  // Get appointment for specific slot
  const getAppointmentForSlot = (
    date: Date,
    time: string,
    doctorId: number
  ): Appointment | null => {
    const dateStr = formatDate(date);

    const found = appointments.find((apt) => {
      let aptDate: string;
      if (apt.appointment_date.includes("T")) {
        aptDate = apt.appointment_date.split("T")[0];
      } else {
        aptDate = apt.appointment_date;
      }

      let aptTime: string;
      if (apt.appointment_time.includes("T")) {
        aptTime = apt.appointment_time.split("T")[1].substring(0, 5);
      } else {
        aptTime =
          apt.appointment_time.length > 5
            ? apt.appointment_time.substring(0, 5)
            : apt.appointment_time;
      }

      const matches =
        aptDate === dateStr &&
        aptTime === time &&
        apt.doctor_id === doctorId &&
        apt.status !== "cancelled";

      return matches;
    });

    return found || null;
  };

  // Check if a slot is occupied by any appointment (considering duration)
  const isSlotOccupied = (
    date: Date,
    time: string,
    doctorId: number
  ): Appointment | null => {
    const dateStr = formatDate(date);
    const slotTime = new Date(`${dateStr}T${time}:00`);

    return (
      appointments.find((apt) => {
        if (apt.doctor_id !== doctorId || apt.status === "cancelled") {
          return false;
        }

        let aptDate: string;
        if (apt.appointment_date.includes("T")) {
          aptDate = apt.appointment_date.split("T")[0];
        } else {
          aptDate = apt.appointment_date;
        }

        if (aptDate !== dateStr) {
          return false;
        }

        if (!apt.appointment_time) {
          console.warn(
            `${getIcon("warning")} Appointment missing appointment_time:`,
            apt
          );
          return false;
        }

        let aptTime: string;
        if (apt.appointment_time.includes("T")) {
          aptTime = apt.appointment_time.split("T")[1].substring(0, 5);
        } else {
          aptTime =
            apt.appointment_time.length > 5
              ? apt.appointment_time.substring(0, 5)
              : apt.appointment_time;
        }

        const appointmentStart = new Date(`${dateStr}T${aptTime}:00`);
        const appointmentEnd = new Date(
          appointmentStart.getTime() + apt.duration * 60000
        );

        return slotTime >= appointmentStart && slotTime < appointmentEnd;
      }) || null
    );
  };

  // Get the main appointment that starts at this slot
  const getMainAppointmentForSlot = (
    date: Date,
    time: string,
    doctorId: number
  ): Appointment | null => {
    const dateStr = formatDate(date);
    return (
      appointments.find((apt) => {
        if (!apt.appointment_time) {
          console.warn(
            `${getIcon("warning")} Appointment missing appointment_time:`,
            apt
          );
          return false;
        }

        let aptTime: string;
        if (apt.appointment_time.includes("T")) {
          aptTime = apt.appointment_time.split("T")[1].substring(0, 5);
        } else {
          aptTime =
            apt.appointment_time.length > 5
              ? apt.appointment_time.substring(0, 5)
              : apt.appointment_time;
        }

        return (
          apt.appointment_date === dateStr &&
          aptTime === time &&
          apt.doctor_id === doctorId &&
          apt.status !== "cancelled"
        );
      }) || null
    );
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, appointment: Appointment) => {
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (
    e: React.DragEvent,
    date: Date,
    time: string,
    doctorId: number
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    const dateStr = formatDate(date);
    const occupiedByAppointment = isSlotOccupied(date, time, doctorId);

    if (!occupiedByAppointment && !isPastTime(date, time)) {
      setDragOverSlot({ date: dateStr, time, doctorId });
    }
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = (
    e: React.DragEvent,
    date: Date,
    time: string,
    doctorId: number
  ) => {
    e.preventDefault();

    if (draggedAppointment) {
      const dateStr = formatDate(date);
      const occupiedByAppointment = isSlotOccupied(date, time, doctorId);

      if (!occupiedByAppointment && !isPastTime(date, time)) {
        onAppointmentDrop(draggedAppointment.id, dateStr, time);
      }
    }

    setDraggedAppointment(null);
    setDragOverSlot(null);
  };

  const handleSlotClick = (date: Date, time: string, doctorId: number) => {
    if (isPastTime(date, time)) return;

    const mainAppointment = getMainAppointmentForSlot(date, time, doctorId);
    const occupiedByAppointment = isSlotOccupied(date, time, doctorId);

    if (mainAppointment) {
      onAppointmentClick(mainAppointment);
    } else if (occupiedByAppointment) {
      onAppointmentClick(occupiedByAppointment);
    } else {
      onSlotClick({
        date: formatDate(date),
        time,
        doctorId,
      });
    }
  };

  // Handle right-click on appointments for status menu
  const handleAppointmentRightClick = (
    e: React.MouseEvent,
    appointment: Appointment
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (onAppointmentAction) {
      setStatusMenu({
        appointment,
        position: { x: e.clientX, y: e.clientY },
      });
    }
  };

  const handleStatusMenuClose = () => {
    setStatusMenu(null);
  };

  const handleStatusChange = async (
    action: "confirm" | "arrive" | "complete" | "cancel" | "no_show" | "delete",
    appointmentId: number,
    reason?: string,
    services?: SelectedService[]
  ): Promise<void> => {
    if (onAppointmentAction) {
      await onAppointmentAction(action, appointmentId, reason, services);
    }
    setStatusMenu(null);
  };

  // ADD: Handle book appointment from status menu
  const handleBookAppointment = (prefilledPatient?: {
    id: number;
    name: string;
    phone: string;
  }) => {
    setBookAppointmentModal({
      isOpen: true,
      prefilledPatient,
    });
    setStatusMenu(null);
  };

  // ADD: Handle book appointment modal close
  const handleBookAppointmentModalClose = () => {
    setBookAppointmentModal({
      isOpen: false,
      prefilledPatient: undefined,
    });
  };

  // ADD: Handle book appointment save
  const handleBookAppointmentSave = async (data: AppointmentFormData) => {
    if (onAppointmentSave) {
      await onAppointmentSave(data);
    }
    handleBookAppointmentModalClose();
  };

  // FIXED: Consistent responsive styles with uniform slot widths
  const containerStyle: React.CSSProperties = {
    height: "100%",
    overflow: "auto",
    position: "relative",
    overflowX: "auto",
    overflowY: "auto",
    width: "100%",
    maxWidth: "100vw",
  };

  const headerStyle: React.CSSProperties = {
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "#ffffff",
    borderBottom: "2px solid #e5e7eb",
    width: "max-content",
    minWidth: `${
      80 + filteredDoctors.length * dateColumns.length * slotWidth
    }px`,
  };

  const headerRowStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `80px repeat(${
      filteredDoctors.length * dateColumns.length
    }, ${slotWidth}px)`,
    minHeight: isMobile ? "50px" : "60px",
    width: "100%",
  };

  const timeColumnStyle: React.CSSProperties = {
    padding: isMobile ? "8px 4px" : "12px 8px",
    fontSize: isMobile ? "10px" : "12px",
    color: "#6b7280",
    fontWeight: "500",
    borderRight: "1px solid #e5e7eb",
    background: "#f9fafb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "sticky",
    left: 0,
    zIndex: 11,
  };

  const getHeaderCellStyle = (index: number): React.CSSProperties => ({
    padding: isMobile ? "8px 4px" : "12px 8px",
    textAlign: "center",
    borderRight: "1px solid #e5e7eb",
    background: index % 2 === 0 ? "#f9fafb" : "#ffffff",
    fontSize: isMobile ? "12px" : "14px",
    width: `${slotWidth}px`,
    minWidth: `${slotWidth}px`,
    maxWidth: `${slotWidth}px`,
  });

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `80px repeat(${
      filteredDoctors.length * dateColumns.length
    }, ${slotWidth}px)`,
    width: "max-content",
    minWidth: `${
      80 + filteredDoctors.length * dateColumns.length * slotWidth
    }px`,
  };

  const timeSlotStyle: React.CSSProperties = {
    height: isMobile ? "50px" : "60px",
    borderRight: "1px solid #e5e7eb",
    borderBottom: "1px solid #f3f4f6",
    background: "#f9fafb",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "4px",
    fontSize: isMobile ? "10px" : "11px",
    color: "#6b7280",
    position: "sticky",
    left: 0,
    zIndex: 5,
  };

  const getSlotStyle = (
    date: Date,
    time: string,
    doctorId: number,
    columnIndex: number
  ): React.CSSProperties => {
    const isPast = isPastTime(date, time);
    const timeSlotIndex = timeSlots.indexOf(time); // ADD THIS LINE
    const isCurrentTime =
      isToday(date) && new Date().getHours() === parseInt(time.split(":")[0]);
    const isDragOver =
      dragOverSlot?.date === formatDate(date) &&
      dragOverSlot?.time === time &&
      dragOverSlot?.doctorId === doctorId;

    const occupiedByAppointment = isSlotOccupied(date, time, doctorId);
    const isOccupied = !!occupiedByAppointment;

    // CHANGE THIS SECTION:
    // Option 1: Checkerboard pattern (alternating by both row and column)
    const baseBackground =
      (columnIndex + timeSlotIndex) % 2 === 0 ? "#e3f2fd" : "F5F5F5";

    return {
      height: isMobile ? "50px" : "60px",
      borderRight: "1px solid #e5e7eb",
      borderBottom: "1px solid #f3f4f6",
      position: "relative",
      cursor: isPast ? "not-allowed" : isOccupied ? "pointer" : "pointer",
      background: isDragOver
        ? "#dbeafe"
        : isCurrentTime
        ? "#fef3c7"
        : isPast
        ? baseBackground
        : baseBackground,
      opacity: isPast ? 0.6 : 1,
      minHeight: isMobile ? "50px" : "60px",
      width: `${slotWidth}px`,
      minWidth: `${slotWidth}px`,
      maxWidth: `${slotWidth}px`,
    };
  };

  const appointmentCardStyle = (
    appointment: Appointment
  ): React.CSSProperties => ({
    position: "absolute",
    top: "2px",
    left: "2px",
    right: "2px",
    height: `${Math.max(
      isMobile ? 46 : 52,
      (appointment.duration / 15) * (isMobile ? 50 : 60) - (isMobile ? 4 : 8)
    )}px`,
    background: appointment.status_color,
    color: "#ffffff",
    borderRadius: isMobile ? "4px" : "6px",
    padding: isMobile ? "4px 6px" : "6px 8px",
    fontSize: isMobile ? "10px" : "11px",
    overflow: "hidden",
    cursor: "grab",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
    transition: "all 0.2s ease",
    zIndex: 5,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  });

  const loadingOverlayStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 20,
  };

  if (filteredDoctors.length === 0) {
    return (
      <div
        style={{
          padding: isMobile ? "20px" : "40px",
          textAlign: "center",
          color: "#6b7280",
        }}
      >
        <h3 style={{ fontSize: isMobile ? "16px" : "18px" }}>
          No doctors selected
        </h3>
        <p style={{ fontSize: isMobile ? "14px" : "16px" }}>
          Please select at least one doctor from the sidebar to view
          appointments.
        </p>
      </div>
    );
  }

  return (
    <div
      style={containerStyle}
      className="keep-grid appointment-calendar-container"
    >
      {isLoading && (
        <div style={loadingOverlayStyle}>
          <div
            style={{
              width: isMobile ? "30px" : "40px",
              height: isMobile ? "30px" : "40px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      )}

      {/* Header - FIXED: Now uses consistent slot width layout for all screen sizes */}
      <div style={headerStyle}>
        <div style={headerRowStyle}>
          <div style={timeColumnStyle}>Time</div>
          {dateColumns.map((date, dateIndex) =>
            filteredDoctors.map((doctor, doctorIndex) => {
              const columnIndex =
                dateIndex * filteredDoctors.length + doctorIndex;
              return (
                <div
                  key={`${formatDate(date)}-${doctor.id}`}
                  style={{
                    ...getHeaderCellStyle(columnIndex),
                    background: isToday(date)
                      ? "#eff6ff"
                      : columnIndex % 2 === 0
                      ? "#f9fafb"
                      : "#ffffff",
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "14px",
                      fontWeight: "600",
                      marginBottom: "2px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Dr. {doctor.first_name || doctor.name}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "10px" : "12px",
                      color: "#6b7280",
                    }}
                  >
                    {date.toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  {doctor.specialty && !isMobile && (
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#9ca3af",
                        marginTop: "2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doctor.specialty}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Time Grid - FIXED: Now uses consistent slot width layout for all screen sizes */}
      <div style={gridStyle}>
        {timeSlots.map((time) => (
          <React.Fragment key={time}>
            {/* Time column */}
            <div style={timeSlotStyle}>{formatTime(time)}</div>

            {/* Appointment slots */}
            {dateColumns.map((date, dateIndex) =>
              filteredDoctors.map((doctor, doctorIndex) => {
                const columnIndex =
                  dateIndex * filteredDoctors.length + doctorIndex;
                const mainAppointment = getMainAppointmentForSlot(
                  date,
                  time,
                  doctor.id
                );
                const occupiedByAppointment = isSlotOccupied(
                  date,
                  time,
                  doctor.id
                );

                return (
                  <div
                    key={`${formatDate(date)}-${time}-${doctor.id}`}
                    data-time-slot={time} // ADD THIS
                    data-date={formatDate(date)} // ADD THIS (optional, for debugging)
                    style={getSlotStyle(date, time, doctor.id, columnIndex)}
                    onClick={() => handleSlotClick(date, time, doctor.id)}
                    onDragOver={(e) => {
                      if (!occupiedByAppointment && !isPastTime(date, time)) {
                        handleDragOver(e, date, time, doctor.id);
                      }
                    }}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, date, time, doctor.id)}
                  >
                    {mainAppointment && (
                      <div
                        style={appointmentCardStyle(mainAppointment)}
                        data-appointment-id={mainAppointment.id}
                        draggable={mainAppointment.can_be_rescheduled}
                        onDragStart={(e) => handleDragStart(e, mainAppointment)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAppointmentRightClick(e, mainAppointment);
                        }}
                        title={`${
                          mainAppointment.patient?.first_name || "Unknown"
                        } ${mainAppointment.patient?.last_name || ""}`}
                      >
                        {/* Patient name */}
                        <div
                          style={{
                            fontWeight: "600",
                            lineHeight: "1.2",
                            marginBottom: "2px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {/* Adaptive text based on slot width */}
                          {slotWidth < 90
                            ? mainAppointment.patient?.first_name || "Unknown"
                            : `${
                                mainAppointment.patient?.first_name || "Unknown"
                              } ${mainAppointment.patient?.last_name || ""}`}
                        </div>

                        {/* Custom pricing indicator */}
                        {mainAppointment.has_custom_pricing && (
                          <div
                            style={{
                              background: "#fef3c7",
                              color: "#92400e",
                              padding: "2px 4px",
                              borderRadius: "3px",
                              fontSize: "8px",
                              fontWeight: "600",
                              marginBottom: "2px",
                            }}
                          >
                            {getIcon("payment")} $
                            {mainAppointment.amount_to_collect}
                            {mainAppointment.original_total_fee && (
                              <span
                                style={{
                                  textDecoration: "line-through",
                                  opacity: 0.7,
                                  marginLeft: "4px",
                                  fontSize: "7px",
                                }}
                              >
                                ${mainAppointment.original_total_fee}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Duration - conditional display based on slot width */}
                        {slotWidth >= 100 && (
                          <div
                            style={{
                              opacity: 0.9,
                              fontSize: "9px",
                            }}
                          >
                            {mainAppointment.duration}min
                          </div>
                        )}

                        {/* Phone number - only show on larger slots */}
                        {slotWidth >= 120 && mainAppointment.patient?.phone && (
                          <div
                            style={{
                              opacity: 0.8,
                              fontSize: "7px",
                              marginTop: "auto",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getIcon("phone")}{" "}
                            {mainAppointment.patient.phone.slice(-4)}
                          </div>
                        )}

                        {/* Status indicator */}
                        <div
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            width: "4px",
                            height: "4px",
                            borderRadius: "50%",
                            background:
                              mainAppointment.status === "confirmed"
                                ? "#10b981"
                                : mainAppointment.status === "completed"
                                ? "#6b7280"
                                : mainAppointment.status === "cancelled"
                                ? "#ef4444"
                                : "#f59e0b",
                          }}
                        />
                      </div>
                    )}

                    {/* Show occupied indicator */}
                    {!mainAppointment && occupiedByAppointment && (
                      <div
                        style={{
                          position: "absolute",
                          top: "4px",
                          left: "4px",
                          right: "4px",
                          bottom: "4px",
                          background: `${occupiedByAppointment.status_color}20`,
                          border: `1px dashed ${occupiedByAppointment.status_color}60`,
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          color: occupiedByAppointment.status_color,
                          pointerEvents: "none",
                        }}
                      >
                        {getIcon("forward")}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Simplified CSS with consistent width calculations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
           /* Highlight animation for time slots */
          [data-time-slot] {
            transition: background-color 0.3s ease;
          }
          
          /* Smooth scrolling for calendar container */
          .calendar-container {
            scroll-behavior: smooth;
          }
          
          @media (prefers-reduced-motion: reduce) {
            .calendar-container {
              scroll-behavior: auto;
            }
          }
          * {
            box-sizing: border-box;
          }
          
          /* Prevent horizontal page scroll */
          body, html {
            overflow-x: hidden;
            max-width: 100vw;
          }
          
          /* Container constraints */
          .appointment-calendar-container {
            width: 100%;
            max-width: 100vw;
            overflow-x: auto;
            box-sizing: border-box;
          }
          
          /* Custom scrollbar styling */
          .appointment-calendar-container::-webkit-scrollbar {
            height: 8px;
          }
          
          .appointment-calendar-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          
          .appointment-calendar-container::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
          }
          
          .appointment-calendar-container::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          
          /* Mobile specific adjustments */
          @media (max-width: 768px) {
            .appointment-calendar-container::-webkit-scrollbar {
              height: 6px;
            }
          }
          
          /* Tablet specific adjustments */
          @media (min-width: 769px) and (max-width: 1024px) {
            .appointment-calendar-container::-webkit-scrollbar {
              height: 8px;
            }
          }
          
          /* Touch targets - ensure minimum touch size */
          @media (max-width: 768px) {
            .appointment-slot {
              min-height: 44px;
              min-width: 44px;
            }
            
            button {
              min-height: 44px;
              min-width: 44px;
            }
          }
          
          /* Prevent text selection on drag */
          .appointment-card {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          
          /* Hover effects for desktop */
          @media (min-width: 1025px) {
            .appointment-slot:hover {
              background-color: #f8fafc;
              transition: background-color 0.2s ease;
            }
            
            .appointment-card:hover {
              transform: scale(1.02);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
              transition: all 0.2s ease;
            }
          }
          
          /* Accessibility improvements */
          .appointment-slot:focus,
          .appointment-card:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }
          
          /* High contrast mode support */
          @media (prefers-contrast: high) {
            .appointment-card {
              border: 2px solid currentColor;
            }
          }
          
          /* Reduced motion support */
          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }
          
          /* Print styles */
          @media print {
            .appointment-calendar-container {
              overflow: visible !important;
              max-width: none !important;
            }
          }
        `}
      </style>

      {/* Status Menu */}
      {statusMenu && onAppointmentAction && (
        <AppointmentStatusMenu
          appointment={statusMenu.appointment}
          position={statusMenu.position}
          onClose={handleStatusMenuClose}
          onViewDetails={onAppointmentClick}
          onStatusChange={handleStatusChange}
          onSaveCustomPricing={onSaveCustomPricing}
          onBookAppointment={handleBookAppointment}
          doctors={doctors}
          patients={patients}
        />
      )}

      {/* Book Appointment Modal */}
      {bookAppointmentModal.isOpen && (
        <AppointmentModal
          isOpen={bookAppointmentModal.isOpen}
          mode="create"
          appointment={null}
          selectedSlot={null}
          prefilledPatient={bookAppointmentModal.prefilledPatient}
          doctors={doctors}
          patients={patients}
          onSave={handleBookAppointmentSave}
          onEdit={onAppointmentEdit || (() => {})}
          onDelete={onAppointmentDelete}
          onClose={handleBookAppointmentModalClose}
          onSaveCustomPricing={onSaveCustomPricing}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default AppointmentCalendar;
